import { ICustomerOption } from '~/common/types';
import {
  Flex,
  Icon,
  Select,
  SelectProps,
  Text,
  TypeAhead,
  TypeAheadFetcher,
} from '@wyz/ui';
import { OptionProps, SingleValueProps, components } from 'react-select';
import { Trans, useI18n } from '~/common/context';
// eslint-disable-next-line @wyz/module/no-relative-import
import './selector.scss';

const customerFilterer = (customer: ICustomerOption, searchQuery: string) => {
  return [customer.label, customer.value, customer.city, customer.zipCode].some(
    (val) =>
      val?.trim().toLowerCase().includes(searchQuery.trim().toLowerCase()),
  );
};
function CustomOption(props: OptionProps<ICustomerOption, false>) {
  return (
    <span
      // @ts-expect-error data-cy is not a valid attr
      data-cy={`${String(props.selectProps['data-cy'] ?? props.selectProps.id)}.${props.data.name}`}
      key={props.innerProps.key}
    >
      <components.Option {...props}>
        <div>
          <Flex alignItems='center' justifyContent='between'>
            <Text heading='xs' color='secondary' weight='semi-bold'>
              {props.data.label}
            </Text>
            {props.isSelected && (
              <Icon
                icon='check-mark-line'
                size={'sm'}
                className='text-primary'
              />
            )}
          </Flex>
          <Text heading='xs' color='secondary-gray'>
            {props.data.city ?? '-'}
          </Text>
        </div>
      </components.Option>
    </span>
  );
}

function CustomSingleValueProps(
  props: SingleValueProps<ICustomerOption, false>,
) {
  return (
    <components.SingleValue
      {...props}
      innerProps={{
        ...props.innerProps,
        // @ts-expect-error data-cy is not a valid attr
        'data-cy': `${String(props.selectProps['data-cy'] ?? props.selectProps.id)}.value`,
      }}
    >
      <div className='d-flex align-items-center space-x-2'>
        <span className='fs-lg'>{props.data.label}</span>
        <span>{'-'}</span>
        <span className='fs-sm'>{props.data.city ?? '?'}</span>
      </div>
    </components.SingleValue>
  );
}

type Props = Pick<
  SelectProps<false, ICustomerOption>,
  'isLoading' | 'isDisabled'
> & {
  value?: ICustomerOption;
  options?: Array<ICustomerOption>;
  onChange?: (selectedOption: ICustomerOption | null) => void;
  /**
   * When set, options are searched server side (autocomplete) instead of being
   * filtered client side from an already fetched list. `options` is then ignored.
   */
  fetcher?: TypeAheadFetcher<ICustomerOption>;
  defaultOptionsFetcher?: (options: {
    signal: AbortSignal;
  }) => Promise<Array<ICustomerOption>>;
  name: string;
};
function BaseCustomerSelector({
  fetcher,
  defaultOptionsFetcher,
  options = [],
  onChange,
  name,
  ...props
}: Props) {
  const { t } = useI18n();

  const sharedProps = {
    placeholder: (
      <Trans i18nKey='app.common.datatable.filter.select.placeholder' />
    ),
    size: 'md' as const,
    className: 'me-2 customer-selector',
    components: {
      Option: CustomOption,
      SingleValue: CustomSingleValueProps,
    },
    showCount: (total: number) =>
      t('app.common.datatable.pagination.showing_result', { total }),
    isClearable: true,
  };

  return (
    <Flex alignItems='center'>
      {fetcher ? (
        <TypeAhead<false, ICustomerOption>
          {...sharedProps}
          {...props}
          name={name}
          id={name}
          fetcher={fetcher}
          defaultOptionsFetcher={defaultOptionsFetcher}
          value={props.value}
          onChange={(selectedOption) => onChange?.(selectedOption)}
        />
      ) : (
        <Select<false, ICustomerOption>
          {...sharedProps}
          {...props}
          name={name}
          filterOption={(option, inputValue) =>
            Boolean(customerFilterer(option.data, inputValue))
          }
          data-cy={`${name}`}
          limit={20}
          options={options}
          onChange={(selectedOption) => onChange?.(selectedOption)}
        />
      )}
    </Flex>
  );
}

export default BaseCustomerSelector;
